import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Eye, EyeSlash } from '@phosphor-icons/react'

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [errorType, setErrorType] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [orgData, setOrgData] = useState(null)
  const orgId = searchParams.get('org')

  const registerForPushNotifications = async (userId) => {
    try {
      if (typeof window !== 'undefined' && window.ExpoNotifications) {
        const Notifications = window.ExpoNotifications
        const { status } = await Notifications.requestPermissionsAsync()
        
        if (status !== 'granted') {
          return null
        }

        const tokenData = await Notifications.getExpoPushTokenAsync()
        const token = tokenData.data
        
        if (token) {
          const { error: updateError } = await supabase
            .from('users')
            .update({ push_token: token })
            .eq('id', userId)

          if (updateError) {
            console.error('Failed to save push token:', updateError)
          }
          
          return token
        }
      }
    } catch (error) {
      console.error('Push notification registration error:', error)
    }
    return null
  }

  useEffect(() => {
    if (orgId) {
      fetchOrgData(orgId)
    }
  }, [orgId])

  const fetchOrgData = async (orgIdParam) => {
    try {
      const { data } = await supabase
        .from('organizations')
        .select('name, logo_url')
        .eq('id', orgIdParam)
        .single()

      if (data) setOrgData(data)
    } catch (error) {
      console.error('Error fetching org data:', error)
    }
  }

  const validateForm = () => {
    if (!email.trim()) {
      setError('Email is required')
      return false
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address')
      return false
    }

    if (!password) {
      setError('Password is required')
      return false
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return false
    }

    return true
  }

  const getNavigationPath = (userData, isOrgAdmin, isMobile) => {
    if (userData.is_super_admin) {
      return '/super-admin/organizations'
    }
    
    const hasLeadership = userData.leadership_level === 'team' || 
                         userData.leadership_level === 'group' || 
                         userData.leadership_level === 'subgroup'
    const isMember = userData.leadership_level === 'member'
    const hasOtherRole = hasLeadership || isMember
    
    if (isMobile && (isOrgAdmin || userData.leadership_level === 'organization') && !hasOtherRole) {
      return '/no-mobile-access'
    }
    
    if (!isMobile && (isOrgAdmin || userData.leadership_level === 'organization')) {
      return '/admin'
    }
    
    if (userData.leadership_level === 'team') {
      return '/team-leader/home'
    } else if (userData.leadership_level === 'group') {
      return '/group-leader/home'
    } else if (userData.leadership_level === 'subgroup') {
      return '/subgroup-leader/home'
    }
    
    return '/member/home'
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setErrorType('')

    if (!validateForm()) {
      setErrorType('auth')
      return
    }

    setLoading(true)

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      })

      if (authError) {
        setErrorType('auth')
        if (authError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.')
        } else if (authError.message.includes('Email not confirmed')) {
          setError('Please verify your email address before logging in.')
        } else {
          setError(authError.message)
        }
        return
      }

      if (data?.user) {
        localStorage.removeItem('isLocked')
        localStorage.removeItem('lastActivity')

        const { data: userData, error: userDataError } = await supabase
          .from('users')
          .select('role, leadership_level, is_super_admin, org_id')
          .eq('id', data.user.id)
          .single()

        if (userDataError || !userData) {
          await supabase.auth.signOut()
          setErrorType('deleted')
          setError('Your account has been removed from the system. Please contact your organization administrator if you believe this is a mistake.')
          return
        }

        let isOrgAdmin = false
        if (userData.org_id) {
          const { data: adminData } = await supabase
            .from('organization_admins')
            .select('is_owner')
            .eq('user_id', data.user.id)
            .eq('org_id', userData.org_id)
            .maybeSingle()
          
          isOrgAdmin = !!adminData
        }

        const isMobile = window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        const navigationPath = getNavigationPath(userData, isOrgAdmin, isMobile)
        
        registerForPushNotifications(data.user.id).catch(err => {
          console.error('Background push registration failed:', err)
        })
        
        navigate(navigationPath)
      }
    } catch (error) {
      console.error('Login error:', error)
      setErrorType('auth')
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo@3x.png" 
              alt="App Logo"
              className="w-16 h-16 rounded-xl shadow-lg object-contain"
            />
          </div>

          <h2 className="text-3xl font-bold text-gray-900">Sign in to your account</h2>
          
          {orgData && (
            <p className="text-lg font-semibold text-gray-900 mt-2">{orgData.name}</p>
          )}
          
          <p className="mt-2 text-sm text-gray-600">
            Enter your credentials to access your dashboard
          </p>
        </div>

        <form onSubmit={handleLogin} className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow">
          {error && errorType === 'auth' && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <div>
                <p className="font-semibold">Authentication Failed</p>
                <p>{error}</p>
              </div>
            </div>
          )}

          {error && errorType === 'deleted' && (
            <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <div>
                <p className="font-semibold">Account Not Found</p>
                <p>{error}</p>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email address *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setError('')
                setErrorType('')
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="you@example.com"
              disabled={loading}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <Link
                to={orgId ? `/forgot-password?org=${orgId}` : "/forgot-password"}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Forgot Password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError('')
                  setErrorType('')
                }}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                disabled={loading}
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : (
              'Sign into your account'
            )}
          </button>

          <p className="text-center text-gray-600 text-sm mt-6">
            Don't have an account? Contact your administrator for an invitation link.
          </p>
        </form>
      </div>
    </div>
  )
}